// Copyright (c) 2025 WiseTime. All rights reserved.

import { concat, Observable } from "rxjs"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { catchError, filter, map, mergeMap, withLatestFrom } from "rxjs/operators"
import { create } from "@bufbuild/protobuf"
import * as Slice from "../slices/timeline"
import * as Actions from "../actions"
import { showSnackBar } from "../../snackbar/actions"
import type { RootState } from "../../../app/store"
import { getTimesheetFieldLabel } from "../timesheet/timesheet-fields"
import { Code, from, Init, isConnectError, timelineService } from "../../../app/rpc"
import {
  ClearRowTimesheetFieldRequestSchema,
  ClearRowTimesheetFieldResponse,
} from "../../../generated/wisetime/user/timeline/v4/timeline_pb"

type ClearRowTimesheetFieldRpc = (request: Init<typeof ClearRowTimesheetFieldRequestSchema>) => Observable<ClearRowTimesheetFieldResponse>

export const clearRowTimesheetField = (clearRowTimesheetFieldRpc: ClearRowTimesheetFieldRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.clearRowTimesheetField.match),
      withLatestFrom(state$),
      mergeMap(([action, state]) => clearRowTimesheetFieldRpc(action.payload)
        .pipe(
          map(response => response.timeRows.map(timeRow => timeRow.id)),
          map((timeRowIds) => Slice.clearRowTimesheetField({
            timeRowIds,
            siteId: action.payload.siteId ?? "",
            timesheetFieldId: action.payload.timesheetFieldId ?? "",
          })),
          catchError(e => {
            const timesheetField = state.timeline.timesheetFieldsCache[action.payload.timesheetFieldId ?? ""]
            const fieldName = timesheetField
              ? getTimesheetFieldLabel(timesheetField)
              : t({ id: "msg_timesheet_field", message: "Timesheet field" }).toLowerCase()
            return handleError(e, fieldName)
          }),
        )
      ),
    )

const handleError = (e: unknown, fieldName: string): Observable<Action> => {
  const actions: Action[] = []
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.NotFound) {
      actions.push(
        showSnackBar({
          message: t({ 
            id: "msg_clear_row_timesheet_field_not_found", 
            message: "Failed to clear {fieldName} value, {fieldName} not found.",
            values: { fieldName },
          }),
          messageType: "ERROR",
        }),
        Actions.onTimelineOutOfSync(),
      )
      return concat(actions)
    }
    if (code === Code.PermissionDenied) {
      actions.push(showSnackBar({
        message: t({ 
          id: "msg_clear_row_timesheet_field_permission_denied", 
          message: "Failed to clear {fieldName} value. You do not have permission.",
          values: { fieldName },
        }),
        messageType: "ERROR",
      }))
      return concat(actions)
    }
    if (code === Code.FailedPrecondition) {
      actions.push(
        showSnackBar({
          message: t({ 
            id: "msg_clear_row_timesheet_field_failed_precondition", 
            message: "Failed to clear {fieldName} value. Time row may be out of sync.",
            values: { fieldName },
          }),
          messageType: "ERROR",
        }),
        Actions.onTimelineOutOfSync(),
      )
      return concat(actions)
    }
  }
  actions.push(showSnackBar({
    message: t({
      id: "msg_clear_row_timesheet_field_failed",
      message: "Failed to clear {fieldName} value. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
      values: { fieldName },
    }),
    messageType: "ERROR",
  }))
  return concat(actions)
}

const clearRowTimesheetFieldRpc = (request: Init<typeof ClearRowTimesheetFieldRequestSchema>): Observable<ClearRowTimesheetFieldResponse> =>
  from(() => timelineService.clearRowTimesheetField(create(ClearRowTimesheetFieldRequestSchema, request)))

export default clearRowTimesheetField(clearRowTimesheetFieldRpc)
